import { useState } from 'react';
import CloseTreeBarIcon from '@shared/assets/images/close-tree-bar.svg';
import OpenTreeBarIcon from '@shared/assets/images/open-tree-bar.svg';

import styles from '@pages/admin/organization/components/OrganizationAdminTree/OrganizationAdminTree.module.scss';
/**
 * 조직 트리 생성
 */
function buildOrgTree(list) {
  const nodeMap = new Map();
  const roots = [];

  list.forEach((item) => {
    nodeMap.set(item.teamCode, {
      key: item.teamCode,
      label: item.team,
      nodeType: 'TEAM',
      position: item.position,
      managerName: item.name,
      raw: {
        ...item,
        parent: null, // 상위 부서명도 같이 넘김
      },
      children: [],
    });
  });

  // 2. 부모-자식 연결
  list.forEach((item) => {
    const node = nodeMap.get(item.teamCode);

    if (item.departmentCode && item.departmentCode !== 0) {
      const parentNode = nodeMap.get(item.departmentCode);
      if (parentNode) {
        //  부모 raw 주입
        node.raw.parent = {
          teamCode: parentNode.raw.teamCode,
          team: parentNode.raw.team,
        };

        parentNode.children.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // 3. 같은 부모를 가진 형제 노드 정렬 (children)
  nodeMap.forEach((node) => {
    if (node.children.length > 0) {
      node.children.sort((a, b) => {
        const aSort = a.raw?.sort ?? 0;
        const bSort = b.raw?.sort ?? 0;
        return aSort - bSort;
      });
    }
  });

  // 4. 최상위 루트 노드 정렬
  roots.sort((a, b) => {
    const aSort = a.raw?.sort ?? 0;
    const bSort = b.raw?.sort ?? 0;
    return aSort - bSort;
  });

  return roots;
}

/**
 * 회사 루트 노드 생성
 */
function buildCompanyTree(company, teamList) {
  return {
    key: `company-${company.companyCode}`,
    label: `${company.companyName} (${company.nick})`,
    managerName: `${company.ceoName}`,
    position: `${company.position}`,
    enumber: `${company.enumber}`,

    nodeType: 'COMPANY',
    raw: company,
    children:
      teamList && teamList.length > 0
        ? buildOrgTree(teamList)
        : [
            {
              key: `empty-${company.companyCode}`,
              label: '조직 정보가 없습니다.',
              children: [],
            },
          ],
  };
}

/**
 * 조직 노드 (재귀)
 */
function OrgNode({
  node,
  depth = 0,
  onSelectNode, // 노드 선택
  isLast = false, // 마지막 노드인지 여부
  selectedNode, // 선택한 노드
  addingNodeKey,
  setAddingNodeKey,
}) {
  const isSelected = selectedNode?.key === node.key;
  // 회사 루트만 defaultOpen 적용, 하위는 기본 닫힘
  const [open, setOpen] = useState(() => depth === 0);
  const hasChildren = node.children && node.children.length > 0;

  const handleSelect = () => {
    // 기존 노드 선택시 부서추가중 지움
    if (addingNodeKey) {
      setAddingNodeKey(null);
    }
    onSelectNode(node);
  };

  return (
    <div
      className={`${styles.node} 
      ${isLast ? styles.lastNode : ''} 
      ${depth === 0 ? styles.rootNode : ''}`}
    >
      <div
        className={`${styles.nodeHeader} ${isSelected ? styles.selected : ''}`}
        onClick={handleSelect}
      >
        {hasChildren ? (
          <div className={styles.toggleIconContainer}>
            <img
              src={open ? CloseTreeBarIcon : OpenTreeBarIcon}
              alt="toggle"
              className={styles.toggleIcon}
              onClick={(e) => {
                e.stopPropagation(); // 헤더 클릭과 분리
                setOpen((prev) => !prev);
              }}
            />
          </div>
        ) : (
          <span className={styles.leafMark}></span>
        )}

        <span className={`${styles.label} ${depth === 0 ? styles.rootLabel : ''}`}>
          {node.label}
          <span className={styles.ceoName}>
            {node.position} {node.managerName}
          </span>
        </span>
        {/* 추가 버튼 */}
        {depth <= 3 && (
          <button
            type="button"
            className={styles.addButton}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
              setAddingNodeKey(node.key);

              onSelectNode({
                nodeType: 'TEAM_CREATE',
                parent: node,
              });
            }}
          >
            + 추가
          </button>
        )}
      </div>

      {open && (
        <div className={styles.childrenContainer}>
          {hasChildren &&
            node.children.map((child, index) => (
              <OrgNode
                key={child.key}
                node={child}
                depth={depth + 1}
                onSelectNode={onSelectNode}
                isLast={index === node.children.length - 1}
                selectedNode={selectedNode}
                addingNodeKey={addingNodeKey}
                setAddingNodeKey={setAddingNodeKey}
              />
            ))}
          {/* 추가중 임시 라인 */}
          {addingNodeKey === node.key && (
            <div className={styles.addingRow}>새 부서 추가 중입니다</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 *  조직 트리 전용 컴포넌트
 */
export default function OrganizationAdminTree({
  companies,
  teamQueries,
  onSelectNode,
  selectedNode,
  addingNodeKey,
  setAddingNodeKey,
}) {
  return (
    <>
      {companies.map((company) => {
        // const teamList = teamQueries[index]?.data ?? [];
        const companyTree = buildCompanyTree(company, teamQueries);

        return (
          <div key={companyTree.key}>
            <OrgNode
              node={companyTree}
              onSelectNode={onSelectNode}
              selectedNode={selectedNode}
              addingNodeKey={addingNodeKey} // 추가중인 부모 노드
              setAddingNodeKey={setAddingNodeKey}
            />
          </div>
        );
      })}
    </>
  );
}
